/**
 * ImageMessage Component
 *
 * Displays an image inline in the Solomon conversation with action buttons
 * for Save, Reject, and Download. Supports loading state during generation.
 */

import { useState, useCallback } from 'react';

import { cn } from '@pray/shared/utils/styles';

import { PROFILE_SLOTS, type SlotType } from '../../utils/imageUtils';

import styles from './ImageMessage.module.scss';

// ============================================================================
// Types
// ============================================================================

export interface ImageMessageProps {
  /** The image URL to display */
  imageUrl?: string;
  /** Target slot for this image */
  targetSlot: SlotType;
  /** Whether the image is currently being generated */
  isGenerating?: boolean;
  /** Whether the image has been saved */
  isSaved?: boolean;
  /** Called when user clicks Save */
  onSave?: () => void;
  /** Called when user clicks Reject */
  onReject?: () => void;
  /** Called when user clicks Download */
  onDownload?: () => void;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Error message when save fails */
  saveError?: string | null;
  /** Message to display above the image */
  message?: string;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Icons
// ============================================================================

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.spinner}
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="31.4 31.4"
        strokeDashoffset="0"
      />
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

export function ImageMessage({
  imageUrl,
  targetSlot,
  isGenerating = false,
  isSaved = false,
  onSave,
  onReject,
  onDownload,
  isSaving = false,
  saveError = null,
  message,
  className,
}: ImageMessageProps) {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const slotConfig = PROFILE_SLOTS[targetSlot];
  const slotLabel = slotConfig?.shortLabel || 'Profile';

  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  const handleImageClick = useCallback(() => {
    if (imageUrl && !isGenerating) {
      setIsExpanded(true);
    }
  }, [imageUrl, isGenerating]);

  const handleCloseExpanded = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const handleDownload = useCallback(() => {
    if (imageUrl && onDownload) {
      onDownload();
    } else if (imageUrl) {
      // Default download behavior
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `solomon-${targetSlot}-${Date.now()}.png`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [imageUrl, onDownload, targetSlot]);

  // Determine aspect ratio class based on slot
  const getAspectRatioClass = () => {
    if (targetSlot === 'profile_vertical_image') return styles.vertical;
    if (targetSlot === 'profile_horizontal_image') return styles.horizontal;
    return styles.square;
  };

  return (
    <>
      <div className={cn(styles.container, isSaved && styles.saved, className)}>
        {/* Message text */}
        {message && <p className={styles.message}>{message}</p>}

        {/* Image container */}
        <div className={cn(styles.imageContainer, getAspectRatioClass())}>
          {isGenerating && (
            // Loading state
            <div className={styles.loadingState}>
              <div className={styles.loadingPulse}>
                <div className={styles.loadingIcon}>
                  <SpinnerIcon />
                </div>
                <span>Generating...</span>
              </div>
            </div>
          )}
          {!isGenerating && imageUrl && (
            // Image display
            <button
              type="button"
              className={styles.imageButton}
              onClick={handleImageClick}
              aria-label={`View ${slotLabel} full size`}
            >
              <img
                src={imageUrl}
                alt={`${slotLabel} preview`}
                className={cn(styles.image, isImageLoaded && styles.loaded)}
                onLoad={handleImageLoad}
              />
              {!isImageLoaded && (
                <div className={styles.imagePlaceholder}>
                  <SpinnerIcon />
                </div>
              )}
            </button>
          )}
          {!isGenerating && !imageUrl && (
            // No image state
            <div className={styles.noImage}>
              <span>No image available</span>
            </div>
          )}
        </div>

        {/* Slot badge */}
        <div className={styles.slotBadge}>
          <span className={styles.slotIcon}>📍</span>
          <span>{slotConfig?.label || 'Profile Image'}</span>
        </div>

        {/* Numbered prompts - only show when image is loaded and not yet saved */}
        {imageUrl && isImageLoaded && !isGenerating && !isSaved && (
          <div className={styles.prompts}>
            <button type="button" className={styles.promptOption} onClick={onSave} disabled={isSaving}>
              <span className={styles.promptNumber}>1.</span>
              {isSaving ? 'Saving to profile...' : 'Save to profile'}
            </button>
            <button type="button" className={styles.promptOption} onClick={onReject} disabled={isSaving}>
              <span className={styles.promptNumber}>2.</span>
              Reject and try again
            </button>
            <button type="button" className={styles.promptOption} onClick={handleDownload} disabled={isSaving}>
              <span className={styles.promptNumber}>3.</span>
              Download image
            </button>
          </div>
        )}

        {/* Saved indicator */}
        {isSaved && !saveError && (
          <div className={styles.savedIndicator}>
            <CheckIcon />
            <span>Saved to profile</span>
          </div>
        )}

        {/* Save error indicator */}
        {saveError && (
          <div className={styles.errorIndicator} role="alert">
            <XIcon />
            <span>{saveError}</span>
          </div>
        )}
      </div>

      {/* Expanded image modal */}
      {isExpanded && imageUrl && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <dialog
          className={styles.modal}
          open
          onClick={handleCloseExpanded}
          onKeyDown={(e) => e.key === 'Escape' && handleCloseExpanded()}
          aria-label={`${slotLabel} expanded view`}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <img src={imageUrl} alt={`${slotLabel} expanded`} className={styles.modalImage} />
            <button type="button" className={styles.modalClose} onClick={handleCloseExpanded}>
              <XIcon />
            </button>
          </div>
        </dialog>
      )}
    </>
  );
}

export default ImageMessage;
